import { ChairOutlineIcon } from '@ikyucom/design-system-icons-react'
import { CheckBox } from 'macaron-frontend-ui'
import { twMerge } from 'tailwind-merge'
import { hstack } from '~/styled-system/patterns'
import { FilterChip } from './FilterChip'
import { FilterChipsWithPopover, useClose, useCommit, useDraft, useSetDraft } from './Popover'
import { createColor } from './criteria'
import { CheckboxPopup } from './popup'
import type { FilterCriteria, SeatAttributes as SeatAttributesType, SeatCriteria } from './types'

export type UseSetSeatAttributes = () => (seatAttributes: SeatCriteria) => void

export function SeatAttributes({
  useFilterCriteria,
  useSeatAttributes,
  useSetSeatAttributes,
  useEnabledSeatAttributes,
}: {
  useFilterCriteria: () => FilterCriteria | undefined
  useSeatAttributes: () => SeatAttributesType
  useSetSeatAttributes: UseSetSeatAttributes
  useEnabledSeatAttributes: () => SeatAttributesType
}) {
  const hasSeatAttributes = useSeatAttributes()
  const setSeatAttributes = useSetSeatAttributes()
  const criteria = useFilterCriteria()?.seatTypes
  const enabled = useEnabledSeatAttributes()
  /**
   * 席タイプと選択したかどうかの値をもつオブジェクトを配列に変換
   * [
   *  ['windowSide', true],
   *  ['counter', false],
   *  ['terrace', true],
   * ]
   */
  const attributes = Object.entries(hasSeatAttributes).filter(([_, value]) => value)

  if (attributes.length === 0) {
    return null
  }

  // テーブル席のみの場合は絞り込みを表示しない
  if (attributes.length === 1 && attributes[0][0] === 'table') {
    return null
  }

  if (attributes.length === 1) {
    const color = createColor({
      enabled: enabled?.[attributes[0][0] as keyof SeatAttributesType],
      isActive: criteria?.[attributes[0][0] as keyof SeatCriteria],
    })
    return (
      <FilterChip
        color={color}
        onClick={() =>
          setSeatAttributes({
            [attributes[0][0]]: !criteria?.[attributes[0][0] as keyof SeatCriteria],
          })
        }
      >
        <div className="flex items-center gap-1">
          <ChairOutlineIcon
            width={13}
            height={13}
            className={twMerge(
              criteria?.[attributes[0][0] as keyof SeatCriteria]
                ? 'fill-marron-light'
                : 'fill-neutral-600'
            )}
          />
          {seatAttributeLabel[attributes[0][0] as keyof SeatAttributesType]}
        </div>
      </FilterChip>
    )
  }

  return (
    <SeatAttributesWithPopover
      criteria={criteria}
      seatAttributes={hasSeatAttributes}
      setSeatAttributes={setSeatAttributes}
      enabled={enabled}
    />
  )
}

function SeatAttributesWithPopover({
  criteria,
  seatAttributes,
  setSeatAttributes,
  enabled,
}: {
  seatAttributes: SeatAttributesType
  criteria?: SeatCriteria
  setSeatAttributes: (seatAttributes: SeatCriteria) => void
  enabled: SeatAttributesType
}) {
  const label = createLabel(criteria)
  const color = createColor({
    enabled:
      enabled.counter ||
      enabled.privateRoom ||
      enabled.semiPrivateRoom ||
      enabled.terrace ||
      enabled.table ||
      enabled.windowSide,
    isActive:
      criteria?.counter ||
      criteria?.privateRoom ||
      criteria?.semiPrivateRoom ||
      criteria?.terrace ||
      criteria?.table ||
      criteria?.windowSide,
  })
  return (
    <FilterChipsWithPopover
      trigger={
        <>
          <ChairOutlineIcon
            width={13}
            height={13}
            className={color === 'active' ? 'text-marron-light' : 'text-neutral-600'}
          />
          {label}
        </>
      }
      data={criteria}
      color={color}
    >
      <Body
        criteria={criteria}
        seatAttributes={seatAttributes}
        setSeatAttributes={setSeatAttributes}
      />
    </FilterChipsWithPopover>
  )
}

function Body({
  seatAttributes,
  setSeatAttributes,
}: {
  seatAttributes: SeatAttributesType
  criteria?: SeatCriteria
  setSeatAttributes: (seatAttributes: SeatCriteria) => void
}) {
  const draft = useDraft<SeatCriteria | undefined>()
  const set = useSetDraft<SeatCriteria>()
  const commit = useCommit(setSeatAttributes)
  const close = useClose()

  return (
    <CheckboxPopup update={commit} cancel={close}>
      {seatAttributes.table && (
        <CheckBox
          checked={draft?.table ?? false}
          onChange={() => set((prev) => ({ ...prev, table: !prev.table }))}
          label="テーブル席"
        />
      )}
      {seatAttributes.windowSide && (
        <CheckBox
          checked={draft?.windowSide ?? false}
          onChange={() => set((prev) => ({ ...prev, windowSide: !prev.windowSide }))}
          label="窓際席"
        />
      )}
      {seatAttributes.counter && (
        <CheckBox
          checked={draft?.counter ?? false}
          onChange={() => set((prev) => ({ ...prev, counter: !prev.counter }))}
          label="カウンター席"
        />
      )}
      {seatAttributes.terrace && (
        <CheckBox
          checked={draft?.terrace ?? false}
          onChange={() => set((prev) => ({ ...prev, terrace: !prev.terrace }))}
          label="テラス席"
        />
      )}
      {seatAttributes.privateRoom && (
        <div className={hstack()}>
          <CheckBox
            checked={draft?.privateRoom ?? false}
            onChange={(e) => {
              set((prev) => ({
                ...prev,
                privateRoom: e.target.checked,
                semiPrivateRoom: e.target.checked,
              }))
            }}
            label="個室"
          />
          {seatAttributes.semiPrivateRoom && (
            <span className={hstack({ gap: '1' })}>
              （
              <CheckBox
                disabled={!draft?.privateRoom}
                checked={draft?.semiPrivateRoom ?? false}
                // チェックを入れるときは両方 ON で、外すときは半個室だけを OFF にする
                // 「半個室」だけを選択することはできないように
                onChange={(e) => {
                  if (e.target.checked) {
                    set((prev) => ({
                      ...prev,
                      privateRoom: true,
                      semiPrivateRoom: true,
                    }))
                  } else {
                    set((prev) => ({
                      ...prev,
                      semiPrivateRoom: false,
                    }))
                  }
                }}
                label="半個室を含む"
              />
              ）
            </span>
          )}
        </div>
      )}
    </CheckboxPopup>
  )
}

function createLabel(criteria?: SeatCriteria) {
  if (criteria === undefined) {
    return '席'
  }
  // 「個室（半個室を含む）」を特別扱いするために調整
  // - 個室と半個室が選択されている場合は「個室（半個室を含む）」で1件とみなしたい
  // - 個室だけが選択されている場合は「個室」と表示して当然1件
  // - 半個室だけ選択されている状態はありえない
  const attributes = Object.entries(criteria).filter(([key, v]) => key !== 'semiPrivateRoom' && v)
  if (attributes.length === 0) {
    return '席'
  }
  const label = (function () {
    if (criteria.privateRoom && criteria.semiPrivateRoom) {
      return '個室（半個室を含む）'
    } else if (criteria.privateRoom) {
      return '個室'
    } else {
      return seatAttributeLabel[attributes.reverse()[0][0] as keyof SeatAttributesType]
    }
  })()
  if (attributes.length === 1) {
    return label
  } else {
    return `${label}、他${attributes.length - 1}件`
  }
}

const seatAttributeLabel = {
  windowSide: '窓際席',
  counter: 'カウンター席',
  terrace: 'テラス席',
  privateRoom: '個室',
  semiPrivateRoom: '半個室',
  table: 'テーブル席',
}
